import { useState, useEffect, useRef, ReactNode, CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { DataFlat } from '../../types';
import { useScreenSize } from '../Context';

interface NumberCounterAnimProps {
    value: number;
};
export function NumberCounterAnim({ value } : NumberCounterAnimProps) {
    const [count, setCount] = useState(0);

    const contentRef = useRef<HTMLDivElement>(null);
    const [startAnimation, setStartAnimation] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setStartAnimation(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        if (contentRef.current) {
            observer.observe(contentRef.current);
        };

        return () => { observer.disconnect(); };
    }, []);

    useEffect(() => {
        if (!startAnimation) return;

        const start = performance.now();
        const step = (timestamp: number) => {
            const elapsed = timestamp - start;
            const progress = Math.min(elapsed / (1000), 1);
            const currentCount = Math.floor(progress * value);
            setCount(currentCount);
        
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                setCount(value);
            };
        };

        requestAnimationFrame(step);
    
        return () => {
            setCount(value); // Ensure final value is set after unmounting
        };
    }, [value, startAnimation]);

    return (
        <motion.span ref={contentRef}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.5 }}
        >
            {count}
        </motion.span>
    );
};

export type SlideDirection = "left" | "right" | "top" | "bottom";

/** Fire whileInView before the element fully scrolls into frame (bottom-heavy for downward reading). */
const SLIDE_IN_VIEWPORT_MARGIN = "0px 0px 15% 0px";

function getSlideInViewportAmount(
    screenSize: string,
    amount?: number
): number {
    return amount ?? (screenSize === "Large" ? 0.08 : 0.05);
}

function getSlideInViewport(
    screenSize: string,
    amount?: number,
    margin?: string
) {
    return {
        once: true as const,
        amount: getSlideInViewportAmount(screenSize, amount),
        margin: margin ?? SLIDE_IN_VIEWPORT_MARGIN,
    };
}

function slideOffset(direction: SlideDirection) {
    return {
        x: direction === "left" ? "-50%" : direction === "right" ? "50%" : 0,
        y: direction === "top" ? "-30%" : direction === "bottom" ? "30%" : 0,
    };
}

interface SlideInAnimProps {
    children: ReactNode;
    direction: SlideDirection;
    style?: CSSProperties;
    className?: string;
    /** Stagger when several items share the same scroll trigger (seconds). */
    delay?: number;
    /** Override viewport intersection ratio (0–1) for whileInView. */
    amount?: number;
    /** IntersectionObserver root margin — positive bottom value triggers earlier on scroll. */
    margin?: string;
}
export function SlideInAnim({
    children,
    direction,
    style,
    className = "",
    delay = 0,
    amount,
    margin,
}: SlideInAnimProps) {
    const location = useLocation();
    const { screenSize } = useScreenSize();
    const initialState = {
        ...slideOffset(direction),
        opacity: 0,
    };
    const [anim, setAnim] = useState<DataFlat>(initialState);

    useEffect(() => {
        setAnim({ ...slideOffset(direction), opacity: 0 });
    }, [location, direction]);

    return (
        <motion.div
            className={`min-w-0 w-full ${className}`.trim()}
            style={style}
            initial={anim}
            whileInView={{ x: 0, y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay }}
            viewport={getSlideInViewport(screenSize, amount, margin)}
        >
            {children}
        </motion.div>
    );
}

interface StaggerInViewProps {
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    /** Delay between child entrances (seconds). */
    stagger?: number;
    amount?: number;
    margin?: string;
}

/** One scroll trigger for a group — children stagger in without each waiting for its own viewport slice. */
export function StaggerInView({
    children,
    className = "",
    style,
    stagger = 0.06,
    amount,
    margin,
}: StaggerInViewProps) {
    const { screenSize } = useScreenSize();
    const reduceMotion = useReducedMotion();

    return (
        <motion.div
            className={className}
            style={style}
            initial="hidden"
            whileInView="visible"
            viewport={getSlideInViewport(screenSize, amount, margin)}
            variants={{
                hidden: {},
                visible: { transition: { staggerChildren: reduceMotion ? 0 : stagger } },
            }}
        >
            {children}
        </motion.div>
    );
}

interface StaggerInViewItemProps {
    children: ReactNode;
    direction?: SlideDirection;
    className?: string;
    style?: CSSProperties;
}

export function StaggerInViewItem({
    children,
    direction = "bottom",
    className = "",
    style,
}: StaggerInViewItemProps) {
    const offset = slideOffset(direction);
    const reduceMotion = useReducedMotion();

    return (
        <motion.div
            className={`min-w-0 w-full ${className}`.trim()}
            style={style}
            variants={{
                hidden: reduceMotion ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, ...offset },
                visible: {
                    opacity: 1,
                    x: 0,
                    y: 0,
                    transition: { duration: reduceMotion ? 0 : 0.8 },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

/** Tab panel crossfade + horizontal nudge (Aimma Doctor/Nurse module switcher). */
export function getHealthModulePanelMotion(
    transitionDirection: number,
    reduceMotion: boolean | null
) {
    const xShift = 14;
    const xEnter = transitionDirection > 0 ? xShift : -xShift;
    const xExit = transitionDirection > 0 ? -xShift : xShift;

    if (reduceMotion) {
        return {
            initial: { opacity: 1, x: 0 },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 1, x: 0 },
            transition: { duration: 0 },
        };
    }

    return {
        initial: { opacity: 0, x: xEnter },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: xExit },
        transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const },
    };
}